// AIMS PWA - scan history viewer.
//
// Lists recent QR scans from the local Dexie log (see offline/scanHistory.js):
// identifier badge (A = assetId, C = catNo, ? = unknown), the scanned value, and
// when it was scanned. Every scan is listed - resolved to a real asset or not.
//
// Tapping a row OPENS that asset (navigates to /?assetNo= or /?catNo=), so it's a
// genuine "go back to what I scanned" list. A caller may pass onSelect to
// override that (e.g. to filter instead of navigate); if onSelect returns a
// falsy value the dialog still falls back to opening the asset. Includes Clear.

import React, { useEffect, useState, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { getScanHistory, clearScanHistory } from '../../offline/scanHistory';

const badgeStyle = (idType) => {
  if (idType === 'a') return { label: 'A', bg: 'var(--primary-tint)', fg: 'var(--primary)', title: 'Asset ID' };
  if (idType === 'c') return { label: 'C', bg: 'var(--surface-tint)', fg: 'var(--text)', title: 'Cat No' };
  return { label: '?', bg: 'var(--surface-tint)', fg: 'var(--muted)', title: 'Unknown identifier' };
};

export default function ScanHistoryDialog({ open, onClose, onSelect }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setEntries(await getScanHistory());
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleClear = async () => {
    await clearScanHistory();
    load();
  };

  // Open the asset the row refers to. Callers can intercept via onSelect (return
  // truthy to signal "handled" and skip navigation, e.g. filter-in-place).
  const openEntry = (e) => {
    const handled = onSelect ? onSelect(e) : false;
    if (handled) return;
    if (e.idType === 'a') window.location.href = '/?assetNo=' + encodeURIComponent(e.value);
    else if (e.idType === 'c') window.location.href = '/?catNo=' + encodeURIComponent(e.value);
    // idType '?' (URL with neither param) - nothing reliable to open; no-op.
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { background: 'var(--surface)', borderRadius: 'var(--r, 12px)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box sx={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Scan history</Box>
        <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading…</Box>
        ) : entries.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
            No scans yet. Codes you scan will appear here.
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {entries.map((e) => {
              const b = badgeStyle(e.idType);
              return (
                <Box
                  key={e.id}
                  onClick={() => openEntry(e)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 2, py: 1.25, cursor: e.idType === '?' ? 'default' : 'pointer',
                    borderBottom: '1px solid var(--border)',
                    '&:hover': e.idType === '?' ? {} : { background: 'var(--surface-tint)' },
                  }}
                >
                  <Box title={b.title} sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: '7px',
                    display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
                    background: b.bg, color: b.fg }}>
                    {b.label}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box className="aims-mono" sx={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.value}
                    </Box>
                    <Box sx={{ fontSize: 11, color: 'var(--muted)' }}>
                      {e.ts ? dayjs(e.ts).format('DD/MM/YYYY HH:mm') : ''}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button size="small" color="error" onClick={handleClear} disabled={entries.length === 0} sx={{ textTransform: 'none' }}>
          Clear history
        </Button>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
